// Frontend Logger Service
// Browser-compatible logging with structured output and error handling

export interface LogContext {
  userId?: string;
  sessionId?: string;
  component?: string;
  operation?: string;
  statusCode?: number;
  duration?: number;
  correlationId?: string;
  // Allow additional fields to be passed without TypeScript errors
  [key: string]: unknown;
}

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

class FrontendLogger {
  private readonly environment: string;
  private readonly logLevel: LogLevel;

  constructor() {
    this.environment = import.meta.env.MODE || 'development';
    this.logLevel = this.getLogLevel();
  }

  private getLogLevel(): LogLevel {
    const level = import.meta.env.VITE_LOG_LEVEL || 'info';
    switch (level.toLowerCase()) {
      case 'debug':
        return LogLevel.DEBUG;
      case 'info':
        return LogLevel.INFO;
      case 'warn':
        return LogLevel.WARN;
      case 'error':
        return LogLevel.ERROR;
      default:
        return LogLevel.INFO;
    }
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private formatLog(level: string, message: string, context?: LogContext, error?: Error) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      message,
      service: 'lenderai-frontend',
      environment: this.environment,
      ...context,
      ...(error && {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name,
        },
      }),
    };

    return logEntry;
  }

  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const logEntry = this.formatLog('debug', message, context);
    
    if (this.environment === 'development') {
      console.debug('🐛 [DEBUG]', message, context);
    } else {
      console.debug(JSON.stringify(logEntry));
    }
  }

  info(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const logEntry = this.formatLog('info', message, context);
    
    if (this.environment === 'development') {
      console.info('ℹ️ [INFO]', message, context);
    } else {
      console.info(JSON.stringify(logEntry));
    }
  }

  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const logEntry = this.formatLog('warn', message, context);
    
    if (this.environment === 'development') {
      console.warn('⚠️ [WARN]', message, context);
    } else {
      console.warn(JSON.stringify(logEntry));
    }
  }

  error(message: string, error?: Error, context?: LogContext): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const logEntry = this.formatLog('error', message, context, error);
    
    if (this.environment === 'development') {
      console.error('❌ [ERROR]', message, error, context);
    } else {
      console.error(JSON.stringify(logEntry));
    }
  }

  // Specialized logging methods
  
  apiError(message: string, error: Error, context?: LogContext): void {
    this.error(message, error, {
      ...context,
      category: 'api_error',
    });
  }

  userAction(action: string, context?: LogContext): void {
    this.info(`User action: ${action}`, {
      ...context,
      category: 'user_action',
    });
  }

  performance(operation: string, duration: number, context?: LogContext): void {
    this.info(`Performance: ${operation} took ${duration}ms`, {
      ...context,
      category: 'performance',
      duration,
    });
  }
}

// Export singleton instance
export const Logger = new FrontendLogger();
export default Logger;